import { Button } from '@sunwebgroup/uds-ui/button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useState } from 'react'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import {
  createDeepLinkingCopiedSuccessfullyEvent,
  createDeepLinkingSharingModalOpenedEvent,
} from '@/scopes/prices/domain/analytics'
import styles from './ShareButton.module.css'

export function ShareButton() {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const currentUrl = window.location.href
  const utmParam = 'utm_source=sunweb&utm_medium=website&utm_campaign=deeplink'
  const copyUrl = `${currentUrl}&${utmParam}`
  const { sendEvent } = useAnalytics()
  const userAgent = navigator.userAgent || navigator.vendor
  const isAndroidDevice = /android/i.test(userAgent)

  const copiedSuccessfully = (result: boolean) => {
    sendEvent(createDeepLinkingCopiedSuccessfullyEvent(result))
    setCopied(result)
  }

  const sendOpenedShareModalEvent = () => {
    sendEvent(createDeepLinkingSharingModalOpenedEvent())
  }

  const handleShare = async () => {
    sendOpenedShareModalEvent()
    const shareData = {
      title: intl.formatMessage({ id: 'price_basket_share_native_window_title' }),
      url: copyUrl,
    }
    const canShare = navigator.canShare?.(shareData)
    if (canShare) {
      try {
        await navigator.share(shareData).then(() => {
          if (isAndroidDevice) {
            copiedSuccessfully(true)
          }
        })
      }
      catch {
        copiedSuccessfully(false)
      }
    }
    else {
      setOpen(true)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl)
      copiedSuccessfully(true)
    }
    catch {
      console.warn(`Could not copy ${copyUrl}`)
      copiedSuccessfully(false)
    }
  }

  const handleCloseModal = () => {
    setOpen(false)
    setCopied(false)
  }
  return (
    <>
      <Button variant="secondary" onClick={handleShare}>
        <Button.Icon glyph="SquareArrowUp" />
        {intl.formatMessage({ id: 'price_basket_share_button_text' })}
      </Button>
      <Sheet.Root open={open} onOpenChange={handleCloseModal}>
        <Sheet.Body side="right" bottomSideInMobile>
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'price_basket_share_native_window_title' })}
          />
          <Sheet.Content className={styles.sheetContent}>
            <Typography variant="body-l-bold-snug">{intl.formatMessage({ id: 'price_basket_share_sheet_content_title' })}</Typography>
            <Typography className={styles.shareDescription} variant="body-m-regular-snug">{intl.formatMessage({ id: 'price_basket_share_sheet_content_description' })}</Typography>
            <div className={styles.shareInput}>
              <Typography variant="body-m-regular-snug">{currentUrl}</Typography>
              <div className={styles.opacityGradient} />
            </div>
            <Button variant="secondary" className={cn(styles.copyButton, copied && styles.copyButtonCopied)} onClick={handleCopy}>
              {copied
                ? <Button.Icon glyph="CheckMarkSmall" />
                : <Button.Icon glyph="SquareOnSquare" />}
              {copied ? intl.formatMessage({ id: 'price_basket_share_sheet_copied' }) : intl.formatMessage({ id: 'price_basket_share_sheet_copy_link' })}
            </Button>
          </Sheet.Content>
        </Sheet.Body>
      </Sheet.Root>
    </>
  )
}
